/**
 * AnimatedModal Component
 */

import React, { useState, Fragment, ReactElement, useEffect, useRef, MutableRefObject } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from 'styled-components';
import { ModalProps } from './animatedModal.types';
import { Wrapper, ContentWrapper } from './animatedModal.style';
import Header from './Header/header';
import Overlay from './Overlay/overlay';
import Handler from '../../commonTypes/handler.types';

const closeKeyCodes = {
  esc: 27,
};

const Modal = ({
  children,
  open,
  headerTitle,
  headerButton,
  customHeader,
  overlay,
  overlayOpacity,
  overlayColor,
  width,
  height,
  disableClose,
  fullScreenMobile,
  portalTarget,
  handlers = {},
  position,
  animate,
  animateFrom,
  animationDuration,
  draggable,
  modalFooter,
  className,
  closeButtonRef,
  closeButtonRight,
  callHandlerOnDragClose = false,
  ...otherProps
}: ModalProps) => {
  const theme = useTheme();
  const defaultAnimationDuration: number = 0.3;
  const [animateUnmount, setAnimateUnmount] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [transitionDuration, setTransitionDuration] = useState<string>('');
  const [startDragPosition, setStartDragPosition] = useState<number[]>([]);
  const [dragAnimation, setDragAnimation] = useState<string>('');
  const modalRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

  if (!portalTarget && typeof document !== 'undefined') {
    portalTarget = document.body;
  }

  useEffect(() => {
    if (modalRef.current && draggable) {
      modalRef.current.addEventListener('touchstart', (e) => touchStart(e));
      modalRef.current.addEventListener('touchmove', (e) => touchMove(e));
      modalRef.current.addEventListener('touchend', (e) => touchEnd(e));
    }
  });
  const [modalOpen, setModalVisibility] = useState(open);
  useEffect(() => {
    if (open) {
      setAnimateUnmount(false);
      setModalVisibility(open);
      setTimeout(
        () => {
          if (typeof document !== 'undefined') {
            document.addEventListener('keydown', handleKeyDownEvent);
          }
          if (onOpened) {
            onOpened(modalRef.current);
          }
        },
        (animationDuration || defaultAnimationDuration) * 1000,
      );
    } else {
      setAnimateUnmount(true);
      if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', handleKeyDownEvent);
      }
      setTimeout(
        () => {
          if (modalRef.current) {
            setModalVisibility(open);
          }
        },
        (animationDuration || defaultAnimationDuration) * 1000,
      );
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', handleKeyDownEvent);
      }
    };
  }, [open]);
  const handleKeyDown = (event: KeyboardEvent) => {
    const { keyCode } = event;
    const target = event.target as HTMLInputElement;

    if (keyCode !== closeKeyCodes.esc && target.value && target.tagName.toUpperCase() === 'INPUT') {
      return;
    }

    if (Object.values(closeKeyCodes).includes(keyCode)) {
      closeModalHandler();
    }
  };
  const handleKeyDownEvent = (e: KeyboardEvent) => handleKeyDown(e);

  const touchStart = (event: TouchEvent) => {
    event.stopPropagation();
    setDragging(true);
    setStartDragPosition([event.touches[0].clientX, event.touches[0].clientY]);
  };
  const touchMove = (event: TouchEvent) => {
    if (!startDragPosition.length) {
      return;
    }
    event.preventDefault();
    const currentYPosition: number = event.touches[0].clientY;
    const currentXPosition: number = event.touches[0].clientX;
    const xDiff: number = startDragPosition[0] - currentXPosition;
    const yDiff: number = startDragPosition[1] - currentYPosition;
    const xDiffAbs: number = Math.abs(xDiff);
    const yDiffAbs: number = Math.abs(yDiff);

    if (dragging && yDiffAbs > xDiffAbs && ((!open && yDiff > 0) || (open && yDiff < 0))) {
      const offset: number = startDragPosition[1] - currentYPosition;
      setDragOffset(offset);
    }
  };

  const closeModalHandler = () => {
    if (disableClose) {
      return;
    }
    setAnimateUnmount(true);
    setTimeout(
      () => {
        setModalVisibility(false);
        if (onClosed) {
          onClosed();
        }
      },
      (animationDuration || defaultAnimationDuration) * 1000,
    );
  };

  const touchEnd = (event: TouchEvent) => {
    event.stopPropagation();
    const dragThreshold: number = 100;
    const dragDistance: number = Math.abs(dragOffset);
    const viewportHeight: number | null = typeof window !== 'undefined' ? window.innerHeight : null;
    const dragPercentage: number = viewportHeight ? 1 - dragDistance / viewportHeight : 1;
    const duration: string = `${Math.floor(1000 * dragPercentage)}ms`;
    if (dragDistance >= dragThreshold) {
      if (dragOffset > 0) {
        setModalVisibility(true);
        setDragging(false);
        setDragOffset(0);
        setTransitionDuration(duration);
      } else {
        setModalVisibility(false);
        setDragging(false);
        setDragOffset(0);
        setTransitionDuration(duration);
        if (callHandlerOnDragClose) {
          closeModalHandler();
        }
      }
    } else {
      setDragging(false);
      setDragOffset(0);
    }
  };

  const defaultHandlers: Handler = { onClick: closeModalHandler };
  const completeHandlers: Handler = { ...defaultHandlers };
  const { onClosed, onOpened, ...otherHandlers } = handlers;

  Object.keys(otherHandlers).forEach((key: string) => {
    completeHandlers[key] = (e: Event) => {
      if (defaultHandlers[key]) {
        defaultHandlers[key](e);
      }
      otherHandlers[key](e);
    };
  });

  useEffect(() => {
    if (!dragging) {
      setDragAnimation('');
    }
    setDragAnimation(`
      transform: translate3d(0, ${dragging ? dragOffset * -1 : 0}px, 0);
      webkit-transform: translate3d(0, ${dragging ? dragOffset * -1 : 0}px, 0);
    `);
  }, [dragging, dragOffset, transitionDuration]);
  const modal: ReactElement = (
    <Fragment>
      <Overlay
        open={open}
        showOverlay={overlay}
        opacity={overlayOpacity}
        color={overlayColor}
        onClickHandler={closeModalHandler}
        portalTarget={portalTarget}
        animateOut={animateUnmount}
        animationDuration={animationDuration || defaultAnimationDuration}
      />

      <Wrapper
        theme={theme}
        customWidth={width}
        customHeight={height}
        fullScreenMobile={fullScreenMobile}
        position={position}
        animate={animate}
        animateOut={animateUnmount}
        animateFrom={animateFrom}
        animationDuration={animationDuration || defaultAnimationDuration}
        dragAnimation={dragAnimation}
        ref={modalRef}
        className={className}
        {...otherProps}
      >
        {customHeader || (
          <Header
            title={headerTitle}
            button={headerButton}
            handlers={{ ...completeHandlers }}
            disableClose={disableClose}
            closeButtonRef={closeButtonRef}
            closeButtonRight={closeButtonRight}
          />
        )}
        <ContentWrapper>{children}</ContentWrapper>
        {modalFooter}
      </Wrapper>
    </Fragment>
  );
  if (portalTarget && typeof window !== 'undefined' && modalOpen) {
    return ReactDOM.createPortal(modal, portalTarget);
  }

  return modalOpen ? modal : null;
};

export default Modal;
